import React, { useEffect } from "react";
import logo from "../assets/SVG/Logo_new.svg";
import logoPOS from "../assets/SVG/logoPOS.svg";
import img from "../assets/login_img.png";
import bg from "../assets/about3.png";
import bgSNS from "../assets/SVG/bgSNS.svg";
import SNSlogo from "../assets/SVG/SNS_logo.svg";
import { Link, useNavigate } from "react-router-dom";
import GenericApi from "@/api/genericApi";
import { authStorageUser } from "@/config/constants/StorageKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { RoutesPathList } from "@/config/routes/Routes";
import { ApiResponse, ApiError } from "@/types/Api";
import { AuthResponse, LoginRequest, LoginRequestSchema } from "@/types/Auth";
import { FormError, ValidationErrors } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import { saveData } from "@/utils/localStorage";
// import { wait } from "@/utils/utils";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { formatError } from "@/utils/formatError";
import axios, { AxiosError, isAxiosError } from "axios";
import { generateLoginInputs } from "@/utils/generateInputs";
import FormField from "@/components/CustomForm/FormField";
import { useIsMobile } from "../hooks/useIsMobile";
import { isBitramp, isSNS } from "@/utils/utils";
import bg_Bitramp from "../assets/SVG/bg_Bitramp.svg";
import logo_Bitramp from "../assets/SVG/logo_Bitramp.svg";
import img_Bitramp from "../assets/SVG/img_Bitramp.svg";
import { User } from "@/types/User.tsx";
import useIsInPWA from "@/hooks/useIsInPWA";

const Login: React.FC = () => {
  const isInPWA = useIsInPWA();
  console.log("isInPWA:", isInPWA);
  const navigate = useNavigate();

  const isMobile = useIsMobile();
  const [isStandalone, setIsStandalone] = React.useState(false);

  const [loginError, setLoginError] = React.useState<string | null>(null);

  useEffect(() => {
    const themeClass = isBitramp() ? "theme-bitramp" : "theme-leonext";
    document.body.classList.add(themeClass);

    return () => {
      document.body.classList.remove("theme-bitramp");
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
  });

  useEffect(() => {
    const isInStandaloneMode = window.matchMedia(
      "(display-mode: standalone)"
    ).matches;
    setIsStandalone(isInStandaloneMode);
  }, []);



  const loginInputs = generateLoginInputs();

  console.log(loginError);

  const loginMutation = useMutation<
    ApiResponse<AuthResponse>,
    ApiError<ValidationErrors>,
    LoginRequest
  >({
    mutationFn: (data) =>
      GenericApi.post<AuthResponse>(ApiRoutes.LOGIN, {
        ...data,
      }),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      // await wait(1000);

      try {
        const completeUserInfo = await axios.get<User>(
          `${GenericApi.baseUrl}${ApiRoutes.USERS}/${data?.user?.id}`,
          {
            headers: {
              Authorization: `Bearer ${data?.access}`,
            },
          }
        );

        const isApproved =
          completeUserInfo?.data?.client_info?.status === "approved";

        if (!isApproved && isInPWA) {
          setLoginError("Your account is not approved yet.");
          return;
        }
      } catch (e) {
        setLoginError("An error occurred while logging in.");
        console.log(e);
        return;
      }

      saveData(authStorageUser, data);
      // isMobile || isStandalone
      //   ? navigate(RoutesPathList.NewClientPOS, { replace: true })
      //   : navigate(RoutesPathList.Dashboard, { replace: true });
      if (location.pathname === RoutesPathList.SNSLogin) {
        navigate(RoutesPathList.NewClientSNS, { replace: true });
      } else if (isMobile || isStandalone) {
        navigate(RoutesPathList.NewClientPOS, { replace: true });
      } else {
        navigate(RoutesPathList.Dashboard, { replace: true });
      }
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
    },
  });

  const handleAxiosError = (error: AxiosError<unknown> | null | Error) => {
    if (isAxiosError<FormError>(error)) {
      setError("root", {
        type: "manual",
        message:
          error.response?.data?.detail ?? "An error occurred while logging in",
      });
      return;
    }

    if (isAxiosError<ValidationErrors>(error)) {
      setError("root", {
        type: "manual",
        message: formatError(error, "An error occurred while logging in"),
      });
      return;
    }

    //* Set error message from server
    setError("root", {
      type: "manual",
      message: error?.message ?? "An error occurred while logging in",
    });
  };

  const handleLogin = (loginRequest: LoginRequest) => {
    const { email, password } = loginRequest;
    loginMutation.mutate({ email, password });
  };

  const onSubmit = async (data: LoginRequest) => {
    handleLogin(data);
  };

  return (
    <div
      className={`login  ${
        isSNS() ? "bg-[#1C1C1C] bg-center " : "bg-[#f9f9f9]"
      }`}
      style={{
        backgroundImage: isSNS() ? `url(${bgSNS})` : "none",
      }}
    >
      {isMobile ? (
        !isSNS() ? (
          <div className="bg-[#fff]">
            <div className="w-[17.75rem] h-[4.5rem] mt-[6rem] ">
              <img draggable="false" src={logoPOS} alt="logo" />
            </div>
            <form
              className="flex flex-col gap-[3rem] pt-[4rem] w-[100%]"
              onSubmit={handleSubmit(onSubmit)}
            >
              {loginInputs.map((input) => (
                <div key={input.name} className="flex-c gap-2">
                  <FormField<LoginRequest>
                    type={input.type}
                    placeholder={input.placeholder}
                    name={input.name}
                    register={register}
                    error={errors[input.name as keyof LoginRequest]}
                    valueAsNumber={input.valueAsNumber}
                    label={input.label}
                    required={input.required}
                    labelStyle="login__container__fields__title text-[#05004E] text-[1.1875rem] font-[700]"
                    inputStyle="rounded-[0.625rem] border-[#D9D9D9]"
                  />
                </div>
              ))}
              {loginError && (
                <span className="block text-center text-red-500 pt-4 sm:inline">
                  {loginError}
                </span>
              )}
              <button
                type="submit"
                className="button--blue !mt-[3rem] !w-[10.3125rem] !h-[3.3125rem] !ml-[4rem]"
              >
                {loginMutation.isPending ? "Loading..." : "Login"}
              </button>
            </form>
            {loginMutation.isError ? (
              <span className="block text-red-500 pt-4 sm:inline">
                {errors.root?.message}
              </span>
            ) : null}
          </div>
        ) : (
          <div className="relative w-auto h-hull">
            <div className="w-[17.75rem] h-[4.5rem] mt-[3rem] relative z-20 justify-center flex">
              <img
                draggable="false"
                src={SNSlogo}
                alt="logo"
                className="w-[5.93125rem] h-[7.25563rem]"
              />
            </div>
            <div className="text-white relative z-20 font-[helvetica] text-center text-[1.375rem] font-[300] mt-[7rem]">
              Sign in
            </div>
            <form
              className="flex flex-col gap-[3rem] pt-[4rem] w-[100%] relative z-20"
              onSubmit={handleSubmit(onSubmit)}
            >
              {loginInputs.map((input) => (
                <div key={input.name} className="flex-c gap-2">
                  <FormField<LoginRequest>
                    type={input.type}
                    placeholder={input.placeholder}
                    name={input.name}
                    register={register}
                    error={errors[input.name as keyof LoginRequest]}
                    valueAsNumber={input.valueAsNumber}
                    label={input.label}
                    required={input.required}
                    labelStyle="login__container__fields__title text-[#fff] text-[1rem] font-[400]"
                    inputStyle="login_container_style focus:outline-none focus:ring-0 focus:border-[#363636] text-white"
                  />
                </div>
              ))}
              {loginError && (
                <span className="block text-center text-red-500 pt-4 sm:inline">
                  {loginError}
                </span>
              )}
              <button
                type="submit"
                className="button_pink !mt-[3rem] !w-[10.3125rem] !h-[3.3125rem] !ml-[4rem] text-white font-[helvetica] !font-[500]"
              >
                {loginMutation.isPending ? "Loading..." : "Sign in"}
              </button>
            </form>
            {loginMutation.isError ? (
              <span className="block text-red-500 pt-4 sm:inline">
                {errors.root?.message}
              </span>
            ) : null}
          </div>
        )
      ) : (
        <>
          {isBitramp() ? (
            <img
              draggable="false"
              className="login__bg__img_bitramp"
              src={bg_Bitramp}
              alt="bg"
            />
          ) : (
            <img
              draggable="false"
              className="login__bg__img"
              src={bg}
              alt="bg"
            />
          )}
          <div className="login__left">
            {/* <Link
              to={RoutesPathList.Landing}
              className="login__left__logo cursor-pointer z-[99]"
            >
              {isBitramp() ? (
                <img
                  draggable="false"
                  className="w-[7rem] h-[4rem]"
                  src={logo_Bitramp}
                  alt="logo"
                />
              ) : (
                <img draggable="false" src={logo} alt="logo" />
              )}
            </Link> */}

            <div
              onClick={() => {
                if (isBitramp()) {
                  window.location.href = "https://bitramp.tech";
                } else {
                  navigate(RoutesPathList.Landing);
                }
              }}
              className="login__left__logo cursor-pointer z-[99]"
            >
              <img
                draggable="false"
                className="w-[7rem] h-[4rem]"
                src={isBitramp() ? logo_Bitramp : logo}
                alt="logo"
              />
            </div>
            <div className="login__left__image">
              {isBitramp() ? (
                <img
                  draggable="false"
                  className="login__left__image--resize_bitramp"
                  src={img_Bitramp}
                  alt="img"
                />
              ) : (
                <img
                  draggable="false"
                  className="login__left__image--resize "
                  src={img}
                  alt="img"
                />
              )}
            </div>
            <div className="login__left__text_bitramp">Get Started Now</div>
          </div>
          {isBitramp() ? (
            <div className="login__container !rounded-[1.5625rem] !bg-[#262626] !border !border-[#262626] !shadow-none mr-[2.6rem]">
              <div className="login__container__top !text-white !text-[3rem]">
                <Link
                  to={RoutesPathList.Landing}
                  className="login__container__top__arrow"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="24"
                    viewBox="0 0 15 24"
                    fill="none"
                  >
                    <path
                      d="M13 2L2.81394 11.2601C2.3775 11.6568 2.3775 12.3432 2.81393 12.7399L13 22"
                      stroke="#fff"
                      stroke-width="3"
                      strokeLinecap="round"
                    />
                  </svg>
                </Link>

                <div>Log In</div>
              </div>
              <form
                className="login__container__fields"
                onSubmit={handleSubmit(onSubmit)}
              >
                {loginInputs.map((input) => (
                  <div key={input.name} className="flex-c gap-2">
                    <FormField<LoginRequest>
                      type={input.type}
                      placeholder={input.placeholder}
                      name={input.name}
                      register={register}
                      error={errors[input.name as keyof LoginRequest]}
                      valueAsNumber={input.valueAsNumber}
                      label={input.label}
                      required={input.required}
                      labelStyle="login__container__fields__title !text-white !text-[1.2rem]"
                      inputStyle="login__container__fields__space !bg-[#2B2B2B] !border !border-[#464646] !text-white placeholder:text-[1rem] focus:outline-none focus:ring-0 focus:border-none"
                    />
                  </div>
                ))}
                <button
                  type="submit"
                  className="Primary_button w-[7.3125rem] h-[2.6875rem]"
                >
                  {loginMutation.isPending ? "Loading..." : "Login"}
                </button>
              </form>
              {loginMutation.isError ? (
                <span className="block text-red-500 pt-4 sm:inline">
                  {errors.root?.message}
                </span>
              ) : null}
              <div className="login__container__switch">
                <div className="login__container__switch__text !text-white font-[400]">
                  Don’t have an account?
                </div>
                <Link
                  to={RoutesPathList.Register}
                  className="login__container__switch__link !text-[#8AFEFD]"
                >
                  Sign Up
                </Link>
              </div>
              <Link
                to={RoutesPathList.ForgotPassword}
                className="!text-[#8AFEFD] hover:underline"
              >
                Forgot Password
              </Link>
            </div>
          ) : (
            <div className="login__container">
              <div className="login__container__top !text-[3rem]">
                <Link
                  to={RoutesPathList.Landing}
                  className="login__container__top__arrow"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="24"
                    viewBox="0 0 15 24"
                    fill="none"
                  >
                    <path
                      d="M13 2L2.81394 11.2601C2.3775 11.6568 2.3775 12.3432 2.81393 12.7399L13 22"
                      stroke="#05004E"
                      stroke-width="3"
                      strokeLinecap="round"
                    />
                  </svg>
                </Link>

                <div>Log In</div>
              </div>
              <form
                className="login__container__fields"
                onSubmit={handleSubmit(onSubmit)}
              >
                {loginInputs.map((input) => (
                  <div key={input.name} className="flex-c gap-2">
                    <FormField<LoginRequest>
                      type={input.type}
                      placeholder={input.placeholder}
                      name={input.name}
                      register={register}
                      error={errors[input.name as keyof LoginRequest]}
                      valueAsNumber={input.valueAsNumber}
                      label={input.label}
                      required={input.required}
                      labelStyle="login__container__fields__title !text-[1.2rem]"
                      inputStyle="login__container__fields__space Input placeholder:text-[1rem]"
                    />
                  </div>
                ))}
                <button
                  type="submit"
                  className="Primary_button w-[7.3125rem] h-[2.6875rem]"
                >
                  {loginMutation.isPending ? "Loading..." : "Login"}
                </button>
                {loginError && (
                  <span className="block text-red-500 pt-4 sm:inline">
                    {loginError}
                  </span>
                )}
              </form>
              {loginMutation.isError ? (
                <span className="block text-red-500 pt-4 sm:inline">
                  {errors.root?.message}
                </span>
              ) : null}

              <div className="login__container__switch">
                <div className="login__container__switch__text">
                  Don’t have an account?
                </div>
                <Link
                  to={RoutesPathList.Register}
                  className="login__container__switch__link"
                >
                  Sign Up
                </Link>
              </div>
              <Link
                to={RoutesPathList.ForgotPassword}
                className="text-blue-500 hover:underline"
              >
                Forgot Password
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Login;
